/**
 * Read-time run state, client side.
 *
 * A port of the logic in `src/lib/investor/analysis-status.ts`, for the same
 * reason it exists there: a Supabase isolate killed for memory or wall clock
 * runs no catch, no finally and no unhandledrejection, so the worker that died
 * is exactly the one that cannot record that it died.
 *
 * A stored `researching` is therefore ambiguous between "in flight" and "dead
 * twenty minutes ago". Elapsed time is the only thing that separates them, so
 * the resolution happens on READ and the row stays a faithful record of what was
 * actually written.
 *
 * This mirrors `supabase/functions/_shared/strategic-analysis/status.ts`
 * deliberately: the client must be able to resolve a dead run WITHOUT the server
 * being alive to tell it, so the rule lives on both sides and the window is the
 * same constant.
 */

/**
 * How long a non-terminal run may go without a heartbeat before it is presumed
 * dead. Floor is the paid-plan wall clock (400s) — a background task cannot
 * legally outlive it — plus margin for queueing and clock skew. Tighter would
 * declare a merely-slow run dead, which is the same bug from the other side.
 */
export const STRATEGIC_STALE_AFTER_MS = 600_000;

export type StoredRunStatus =
  | 'queued'
  | 'researching'
  | 'synthesizing'
  | 'validating'
  | 'ready'
  | 'insufficient_evidence'
  | 'failed';

export type ResolvedRunState = StoredRunStatus | 'timed_out' | 'not_generated';

const TERMINAL: ReadonlySet<StoredRunStatus> = new Set<StoredRunStatus>([
  'ready',
  'insufficient_evidence',
  'failed',
]);

export interface StrategicRunRow {
  id: string;
  status: StoredRunStatus;
  /** ISO timestamp of the last write. The heartbeat. */
  updated_at: string;
  failure_code: string | null;
  input_fingerprint: string;
  report: unknown | null;
}

export interface ResolvedRun {
  runId: string | null;
  state: ResolvedRunState;
  /** The clock, not the worker, decided this run is over. */
  presumedDead: boolean;
  failureCode: string | null;
  /** Nonterminal and alive. Nothing else should poll. */
  shouldPoll: boolean;
  /** Ready, but built from superseded inputs. Never a reason to poll. */
  isStale: boolean;
  /** A ready report stays visible while a replacement runs. */
  report: unknown | null;
}

export function resolveStrategicRunState(
  row: StrategicRunRow | null | undefined,
  now: number,
  options: { currentInputFingerprint?: string } = {},
): ResolvedRun {
  if (!row) {
    return {
      runId: null,
      state: 'not_generated',
      presumedDead: false,
      failureCode: null,
      shouldPoll: false,
      isStale: false,
      report: null,
    };
  }

  if (TERMINAL.has(row.status)) {
    // A worker that reached its own error path knows more than a clock does.
    // Never overwrite a real diagnosis with the generic one, however old it is.
    const isStale = Boolean(
      row.status === 'ready' &&
        options.currentInputFingerprint &&
        options.currentInputFingerprint !== row.input_fingerprint,
    );
    return {
      runId: row.id,
      state: row.status,
      presumedDead: false,
      failureCode: row.failure_code,
      shouldPoll: false,
      isStale,
      report: row.report,
    };
  }

  // Strictly greater than: exactly at the window is still working.
  if (now - Date.parse(row.updated_at) > STRATEGIC_STALE_AFTER_MS) {
    return {
      runId: row.id,
      state: 'timed_out',
      presumedDead: true,
      failureCode: 'timed_out',
      shouldPoll: false,
      isStale: false,
      report: null,
    };
  }

  return {
    runId: row.id,
    state: row.status,
    presumedDead: false,
    failureCode: row.failure_code,
    shouldPoll: true,
    // Work in flight against older inputs is superseded, not stale. A "stale"
    // badge on a spinner reads as a broken run.
    isStale: false,
    report: null,
  };
}

// `selectVisibleReport` lived here and is gone. It was the SECOND display
// selection: the hook used it to choose which run's coverage, derivation and
// snapshot captioned the report, while `resolveReportView` independently chose
// whose body was drawn, and nothing made the two agree. `report-state.ts` now
// names the run it selected and every caption is read from that row.
